import { useEffect, useState } from "react";
import { IPOI } from "./matterportViewer3";
import { FaMapMarkerAlt } from "react-icons/fa";
import { MdRotate90DegreesCw } from "react-icons/md";
import { useNavigate } from "react-router-dom";

interface IPOIPanelProps {
  currentPOI?: IPOI;
  savePOIEvent: () => void;
  cancelPOIEvent: () => void;
  isPOISaving?: boolean;
  updatePOILongitude: (value: number) => void;
  updatePOILatitude: (value: number) => void;
  updatePOIAltitude: (value: number) => void;
  updatePOIHeading: (value: number) => void;
  updatePOIFlipX: (value: number) => void;
  updatePOIFlipY: (value: number) => void;
  updatePOIFlipZ: (value: number) => void;
  setPutPOIEnabled: (enabled: boolean) => void;
  setRotatePOIEnabled: (enabled: boolean) => void;
  onChangeModelFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  rotationStartEvent: () => void;
  rotatePOIEnabled: boolean;
  isPutPOIEnabled: boolean;
}

export const POIPanel = (props: IPOIPanelProps) => {
  const {
    currentPOI,
    isPOISaving,
    savePOIEvent,
    cancelPOIEvent,
    updatePOILongitude,
    updatePOILatitude,
    updatePOIAltitude,
    updatePOIHeading,
    updatePOIFlipX,
    updatePOIFlipY,
    updatePOIFlipZ,
    setPutPOIEnabled,
    onChangeModelFile,
    rotationStartEvent,
    setRotatePOIEnabled,
    rotatePOIEnabled,
    isPutPOIEnabled,
  } = props;

  const navigate = useNavigate();

  const [isPOIEnabled, setIsPOIEnabled] = useState(false);
  const [isRotatePOIEnabled, setIsRotatePOIEnabled] = useState(false);
  const [isPOISelected, setIsPOISelected] = useState(false);

  useEffect(() => {
    setIsPOIEnabled(isPutPOIEnabled);
  }, [isPutPOIEnabled]);

  useEffect(() => {
    if (currentPOI && currentPOI.modelUrl) {
      setIsPOISelected(true);
    } else {
      setIsPOISelected(false);
    }
  }, [currentPOI]);

  useEffect(() => {
    setIsRotatePOIEnabled(rotatePOIEnabled);
  }, [rotatePOIEnabled]);

  const togglePOIEnabled = () => {
    setIsPOIEnabled(!isPOIEnabled);
    setPutPOIEnabled(!isPOIEnabled);
  };

  const toggleRotatePOIEnabled = () => {
    setIsRotatePOIEnabled(!isRotatePOIEnabled);
    setRotatePOIEnabled(!isRotatePOIEnabled);
    rotationStartEvent();
  };

  const onCreatePost = () => {
    if (!isPOISelected) return;
    navigate(`/posts/create`, { state: { poiFile: currentPOI } });
  };

  return (
    <>
      <h3>Model Placement</h3>
      <div className="flex flex-wrap justify-evenly">
        <button
          className="flex flex-col content-center justify-center p-2"
          type="button"
          onClick={togglePOIEnabled}
        >
          <FaMapMarkerAlt className="text-4xl mx-auto mb-1" />
          <span className="text-xs">Place: {isPOIEnabled ? "ON" : "OFF"}</span>
        </button>

        <button
          className="flex flex-col content-center justify-center p-2"
          type="button"
          onClick={toggleRotatePOIEnabled}
        >
          <MdRotate90DegreesCw className="text-4xl mx-auto mb-1" />
          <span className="text-xs">
            Rotate: {isRotatePOIEnabled ? "ON" : "OFF"}
          </span>
        </button>
      </div>
      {currentPOI && (
        <div>
          <h4>ID</h4>
          <p className="text-xs mb-4">{currentPOI.id}</p>
          <h4>Longitude</h4>
          <div className="flex content-center justify-between">
            <p className="flex flex-wrap content-center text-xs">
              {currentPOI.longitude}
            </p>
            <div>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILongitude(-0.0005)}
              >
                --
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILongitude(-0.00005)}
              >
                -
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILongitude(0.00005)}
              >
                +
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILongitude(0.0005)}
              >
                ++
              </button>
            </div>
          </div>
          <h4>Latitude</h4>
          <div className="flex content-center justify-between">
            <p className="flex flex-wrap content-center text-xs">
              {currentPOI.latitude}
            </p>
            <div>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILatitude(-0.00001)}
              >
                --
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILatitude(-0.000001)}
              >
                -
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILatitude(-0.000001)}
              >
                +
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOILatitude(0.00001)}
              >
                ++
              </button>
            </div>
          </div>
          <h4>Altitude</h4>
          <div className="flex content-center justify-between">
            <p className="flex flex-wrap content-center text-xs">
              {currentPOI.altitude}
            </p>
            <div>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIAltitude(-1)}
              >
                --
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIAltitude(-0.1)}
              >
                -
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIAltitude(0.1)}
              >
                +
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIAltitude(1)}
              >
                ++
              </button>
            </div>
          </div>
          <h4>Heading</h4>
          <div className="flex content-center justify-between">
            <p className="flex flex-wrap content-center text-xs">
              {currentPOI.heading}
            </p>
            <div>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIHeading(-10)}
              >
                --
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIHeading(-1)}
              >
                -
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIHeading(1)}
              >
                +
              </button>
              <button
                className="text-xs w-6 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIHeading(10)}
              >
                ++
              </button>
            </div>
          </div>
          <h4>Model Rotation</h4>
          <div className="flex content-center justify-between">
            <p className="flex flex-wrap content-center text-xs">Rotate 90°</p>
            <div>
              <button
                className="text-xs w-12 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIFlipX(90)}
              >
                X-Axis
              </button>
              <button
                className="text-xs w-12 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIFlipY(90)}
              >
                Y-Axis
              </button>
              <button
                className="text-xs w-12 m-1 px-1 bg-white text-black rounded-sm"
                type="button"
                onClick={() => updatePOIFlipZ(90)}
              >
                Z-Axis
              </button>
            </div>
          </div>
          <h4>Select Model</h4>
          <p className="mb-4">
            <input
              type="file"
              accept=".glb"
              onChange={(e) => onChangeModelFile(e)}
            />
          </p>

          <div className="flex justify-end space-x-2 mt-4">
            <button
              onClick={cancelPOIEvent}
              className="px-4 py-2 hover:bg-gray-100"
            >
              Cancel
            </button>

            <button
              onClick={savePOIEvent}
              className="px-4 py-2 border rounded-lg bg-[#2fe9ff] text-white hover:bg-[#2fe9ff]-600 flex items-center justify-center"
              disabled={isPOISaving}
            >
              {isPOISaving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
              ) : null}
              {isPOISaving ? "Saving..." : "Save"}
            </button>
          </div>
          <div>
            {isPOISelected && (
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={onCreatePost}
                  className="px-4 py-2 border rounded-lg bg-[#2fe9ff] text-white hover:bg-[#2fe9ff]-600 flex items-center justify-center"
                >
                  Create Post
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
